import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Button, Modal, ModalBody } from 'reactstrap';

import { changePassword as changePasswordAction } from 'Redux/ducks/auth';
import MessageBox from 'Components/Utility/MessageBox';

const messages = defineMessages({
  enter_current_password: {
    id: 'account-password.entercurrentpassword',
    defaultMessage: 'Please enter your current password',
  },
  please_enter_password: {
    id: 'account-password.pleaseenterpassword',
    defaultMessage: 'Please enter a password',
  },
  passwords_match: {
    id: 'account-password.passwordsmatch',
    defaultMessage: 'Please confirm the password - Passwords Do Not Match',
  },
  confirm_password: {
    id: 'account-password.pleaseconfirmpassword',
    defaultMessage: 'Please confirm the password',
  },
  password_six_characters: {
    id: 'account-password.passwordsixcharacters',
    defaultMessage: 'Password must be 6 characters or more',
  },
  passwordupdated: {
    id: 'account-password.passwordupdated',
    defaultMessage: 'Your password has been updated.',
  },
  success: {
    id: 'account-password.success',
    defaultMessage: 'Success',
  },
  errorchangingpassword: {
    id: 'account-password.errorchangingpassword',
    defaultMessage: 'Error Changing Password',
  },
});

const AccountPassword = ({ changePassword, intl: { formatMessage } }) => {
  const [changing, setChanging] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordFail, setPasswordFail] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [exError, setExError] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);

  const resetMessageTimer = () => {
    const displayTime = 1000 * 5;
    setTimeout(() => {
      setPasswordSuccess(false);
      setPasswordFail(false);
      setPasswordError('');
    }, displayTime);
  };

  const submitChangePassword = async (currentPw, newPw, confirmPw) => {
    try {
      await changePassword(currentPw, newPw, confirmPw);
      setChanging(false);
      setPasswordSuccess(true);
      setShowCloseButton(true);
      resetMessageTimer();
    } catch (ex) {
      setPasswordFail(true);
      setPasswordError(ex.message);
      setLoading(false);
      setExError(true);
      resetMessageTimer();
    }
  };

  const click = (submit) => {
    if (!submit) {
      setCurrentPassword('');
      setNewPassword('');
      setPasswordConfirm('');
      setPasswordSuccess(false);
      setPasswordFail(false);
      setPasswordError('');
      setChanging(!changing);
      setLoading(false);
      setShowCloseButton(false);
      return;
    }
    if (currentPassword === '') {
      setPasswordError(true);
      setPasswordError(formatMessage(messages.enter_current_password));
      return;
    }
    if (newPassword === '') {
      setPasswordFail(true);
      setPasswordError(formatMessage(messages.please_enter_password));
      return;
    }
    if (passwordConfirm === '') {
      setPasswordFail(true);
      setPasswordError(formatMessage(messages.confirm_password));
      return;
    }
    if (newPassword !== passwordConfirm) {
      setPasswordFail(true);
      setPasswordError(formatMessage(messages.passwords_match));
      return;
    }

    if (newPassword.length < 6) {
      setPasswordFail(true);
      setPasswordError(formatMessage(messages.password_six_characters));
      return;
    }
    setPasswordSuccess(false);
    setPasswordFail(false);
    setPasswordError('');
    setLoading(true);
    submitChangePassword(currentPassword, newPassword, passwordConfirm);
  };

  const handleChange = (e) => {
    if (e.target.name === 'currentPassword') {
      setCurrentPassword(e.target.value);
    }
    if (e.target.name === 'newPassword') {
      setNewPassword(e.target.value);
    }
    if (e.target.name === 'passwordConfirm') {
      setPasswordConfirm(e.target.value);
    }
    setPasswordFail(false);
  };


  return (
    <React.Fragment>
      <div className="account-table-row">
        <div className="account-table-row-title">
          <FormattedMessage id="account-password.table.title" defaultMessage="Password" />
        </div>
        <div className="account-table-row-content">
          <div className="account-table-details-container">
            {'********'}
          </div>
          <div className="account-table-action-container">
            <button className="account-table-action-button" onClick={() => click(false)}>
              <FormattedMessage id="account-password.setting.button.password" defaultMessage="Change Password" />
            </button>
          </div>
        </div>
      </div>

      <Modal isOpen={changing} toggle={() => click(false)} className="account-modal">
        <ModalBody>
          <div className="account-modal-title">
            <FormattedMessage id="account-password.modal.title" defaultMessage="Change Password" />
          </div>

          {changing &&
            <div className="account-modal-form">
              <div className="form-group">
                <label htmlFor="currentPassword" className="account-modal-form-title">
                  <FormattedMessage id="account-password.form.current-password" defaultMessage="Current Password" />
                </label>
                <div className="account-modal-form-input">
                  <input
                    id="currentPassword"
                    aria-label="current-password"
                    type="password"
                    autoComplete="current-password"
                    name="currentPassword"
                    value={currentPassword}
                    className={classNames({ 'input-error': passwordFail && currentPassword === '' })}
                    onChange={handleChange}
                  />
                  {passwordFail && currentPassword === '' &&
                    <div className="invalid-field">
                      {passwordError}
                    </div>
                  }
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="newPassword" className="account-modal-form-title">
                  <FormattedMessage id="account-password.form.new-password" defaultMessage="New Password" />
                </label>
                <div className="account-modal-form-input">
                  <input
                    id="newPassword"
                    aria-label="new-password"
                    type="password"
                    autoComplete="new-password"
                    name="newPassword"
                    value={newPassword}
                    minLength="8"
                    className={classNames({ 'input-error': passwordFail && currentPassword !== '' && (newPassword === '' || newPassword !== passwordConfirm || newPassword.length < 6) })}
                    onChange={handleChange}
                  />
                  {passwordFail && currentPassword !== '' && (newPassword === '' || newPassword !== passwordConfirm || newPassword.length < 6) &&
                    <div className="invalid-field">
                      {passwordError}
                    </div>
                  }
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="newPassword" className="account-modal-form-title">
                  <FormattedMessage id="account-password.form.confirm-password" defaultMessage="Confirm Password" />
                </label>
                <div className="account-modal-form-input">
                  <input
                    aria-label="password-confirm"
                    type="password"
                    autoComplete="new-password"
                    name="passwordConfirm"
                    value={passwordConfirm}
                    minLength="8"
                    className={classNames({ 'input-error': passwordFail && currentPassword !== '' && (passwordConfirm === '' || newPassword !== passwordConfirm) })}
                    onChange={handleChange}
                  />
                  {passwordFail && currentPassword !== '' && (passwordConfirm === '' || newPassword !== passwordConfirm) &&
                    <div className="invalid-field">
                      {passwordError}
                    </div>
                  }
                </div>
              </div>

              {passwordSuccess &&
                <MessageBox
                  type="success"
                  title={formatMessage(messages.success)}
                  message={formatMessage(messages.passwordupdated)}
                />
              }
              {passwordFail && exError &&
                <MessageBox
                  type="error"
                  title={formatMessage(messages.errorchangingpassword)}
                  message={passwordError}
                />
              }

              <div className="account-modal-form-buttons">
                {!loading && !passwordSuccess && !showCloseButton &&
                  <React.Fragment>
                    <div className="form-button-container">
                      <Button color="primary" onClick={() => click(true)}>
                        <FormattedMessage id="account-password.form.confirm" defaultMessage="Confirm" />
                      </Button>
                    </div>
                    <div className="form-button-container">
                      <Button color="secondary" onClick={() => click(false)}>
                        <FormattedMessage id="account-password.form.cancel" defaultMessage="Cancel" />
                      </Button>
                    </div>
                  </React.Fragment>
                }
                {loading && !passwordSuccess &&
                  <div>
                    <FontAwesomeIcon spin icon={faSpinner} title="Loading" />
                  </div>
                }
                {showCloseButton &&
                  <div className="form-button-container">
                    <Button color="secondary" onClick={() => click(false)}>
                      <FormattedMessage id="account-password.form.close" defaultMessage="Close" />
                    </Button>
                  </div>
                }
              </div>
            </div>
          }
        </ModalBody>

        <div className="close-button-container">
          <button aria-label="close" onClick={() => click(false)}>
            <FontAwesomeIcon icon={faXmark} title="Close modal" />
          </button>
        </div>
      </Modal>
    </React.Fragment>
  );
};

AccountPassword.propsTypes = {
  changePassword: PropTypes.func.isRequired,
};

const mapDispatchToProps = { changePassword: changePasswordAction };

export default connect(null, mapDispatchToProps)(injectIntl(AccountPassword));
